"use client";

import type { PublicHelpSpace } from "@v1/backend/logic/pages/public_help_space_groups";
import { Button } from "@v1/ui/button";
import { IconOrEmoji } from "@v1/ui/icon-or-emoji";
import { cn } from "@v1/ui/utils";
import { BookOpen, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactElement, ReactNode } from "react";
import { useHelpHref } from "./help-link-base";
import {
  type HelpNavPath,
  type HelpTreeNode,
  helpNavPageKey,
  helpNavSpaceKey,
} from "./help-tree";

/** Deepest level with its own sidebar row; level 4+ is listed on its parent page. */
const SIDEBAR_MAX_LEVEL = 3;

export type HelpNavExpansion = {
  /** Reader's choice wins; otherwise `auto` (on the current path). */
  readonly isOpen: (key: string, auto: boolean) => boolean;
  readonly toggle: (key: string, open: boolean) => void;
};

/** Level 1: knowledge space. Name opens its overview, arrow only folds. */
export function HelpNavSpaceItem({
  org_slug,
  space,
  tree,
  path,
  expansion,
}: {
  readonly org_slug: string;
  readonly space: PublicHelpSpace;
  readonly tree: readonly HelpTreeNode[];
  readonly path: HelpNavPath;
  readonly expansion: HelpNavExpansion;
}): ReactElement {
  const key = helpNavSpaceKey(space.space_id);
  const helpHref = useHelpHref(org_slug);
  const isCurrent = path.currentKey === key;
  const onPath = path.pathKeys.has(key) && !isCurrent;
  const open = expansion.isOpen(key, isCurrent || onPath);

  return (
    <li>
      <div
        className={cn(
          "group flex min-h-11 items-center rounded-lg pr-1.5 transition-colors",
          isCurrent ? "bg-action-500/[0.08]" : "hover:bg-muted/60",
        )}
      >
        <Link
          href={helpHref({
            kind: "space",
            spaceId: space.space_id,
          })}
          prefetch={false}
          aria-current={isCurrent ? "page" : undefined}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-3 self-stretch rounded-lg py-2.5 pl-3 pr-1 text-[14.5px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action-500/30",
            isCurrent
              ? "text-action-700"
              : onPath
                ? "text-primary"
                : "text-primary/75 group-hover:text-primary",
          )}
        >
          <span
            aria-hidden
            className="flex h-[18px] w-[18px] shrink-0 items-center justify-center"
          >
            {space.icon ? (
              <IconOrEmoji value={space.icon} fallback="" size={16} />
            ) : (
              <BookOpen className="h-4 w-4" strokeWidth={1.75} />
            )}
          </span>
          <span className="min-w-0 flex-1">{space.name}</span>
        </Link>
        <HelpNavToggle
          title={space.name}
          open={open}
          onToggle={() => expansion.toggle(key, open)}
        />
      </div>
      <HelpNavFold open={open}>
        <ul className="ml-[21px] mt-1 space-y-0.5 border-l border-border pb-1 pl-[10px]">
          {tree.map((node) => (
            <HelpNavPageItem
              key={node.page.page_id}
              org_slug={org_slug}
              node={node}
              level={2}
              path={path}
              expansion={expansion}
            />
          ))}
        </ul>
      </HelpNavFold>
    </li>
  );
}

/** Level 2 or 3. Only level 2 folds; level-3 children live on the page itself. */
function HelpNavPageItem({
  org_slug,
  node,
  level,
  path,
  expansion,
}: {
  readonly org_slug: string;
  readonly node: HelpTreeNode;
  readonly level: 2 | 3;
  readonly path: HelpNavPath;
  readonly expansion: HelpNavExpansion;
}): ReactElement {
  const key = helpNavPageKey(node.page.page_id);
  const helpHref = useHelpHref(org_slug);
  const isCurrent = path.currentKey === key;
  const onPath = path.pathKeys.has(key) && !isCurrent;
  const folds = level < SIDEBAR_MAX_LEVEL && node.children.length > 0;
  const open = folds && expansion.isOpen(key, isCurrent || onPath);
  // Last visible level stands in for a deeper current page.
  const marksDeeper = onPath && level === SIDEBAR_MAX_LEVEL;

  return (
    <li>
      <div
        className={cn(
          "group relative flex items-start rounded-md transition-colors",
          isCurrent ? "bg-action-500/[0.08]" : "hover:bg-muted/60",
        )}
      >
        {isCurrent || marksDeeper ? (
          <span
            aria-hidden
            className={cn(
              "absolute -left-[11px] bottom-1 top-1 w-[2px] rounded-full",
              isCurrent ? "bg-action-500" : "bg-action-500/35",
            )}
          />
        ) : null}
        <Link
          href={helpHref({
            kind: "page",
            pageId: node.page.page_id,
          })}
          prefetch={false}
          aria-current={isCurrent ? "page" : undefined}
          className={cn(
            "min-w-0 flex-1 rounded-md px-3 leading-5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action-500/30",
            level === 2 ? "py-2 text-[13.5px]" : "py-1.5 text-[13px]",
            isCurrent
              ? "font-medium text-action-700"
              : onPath
                ? "font-medium text-primary/90"
                : level === 2
                  ? "text-primary/65 group-hover:text-primary/90"
                  : "text-primary/55 group-hover:text-primary/85",
          )}
        >
          {node.page.title}
        </Link>
        {folds ? (
          <HelpNavToggle
            title={node.page.title}
            open={open}
            onToggle={() => expansion.toggle(key, open)}
            compact
          />
        ) : null}
      </div>
      {folds ? (
        <HelpNavFold open={open}>
          <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-border pb-1 pl-[10px]">
            {node.children.map((child) => (
              <HelpNavPageItem
                key={child.page.page_id}
                org_slug={org_slug}
                node={child}
                level={3}
                path={path}
                expansion={expansion}
              />
            ))}
          </ul>
        </HelpNavFold>
      ) : null}
    </li>
  );
}

function HelpNavToggle({
  title,
  open,
  onToggle,
  compact = false,
}: {
  readonly title: string;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly compact?: boolean;
}): ReactElement {
  const t = useTranslations("public.helpNavTree");
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-expanded={open}
      aria-label={open ? t("zuklappen", { title }) : t("aufklappen", { title })}
      onClick={onToggle}
      className={cn(
        "shrink-0 rounded-md text-primary/40 hover:bg-muted hover:text-primary/75 focus-visible:ring-action-500/30 focus-visible:ring-offset-0",
        compact ? "mr-0.5 mt-[5px] h-[26px] w-[26px]" : "h-7 w-7",
      )}
    >
      <ChevronRight
        aria-hidden
        className={cn(
          "transition-transform duration-200 motion-reduce:transition-none",
          compact ? "h-3.5 w-3.5" : "h-4 w-4",
          open && "rotate-90",
        )}
      />
    </Button>
  );
}

/** Grid-row fold; hidden rows leave the tab order once folded (same as meter-inline-delete). */
function HelpNavFold({
  open,
  children,
}: {
  readonly open: boolean;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div
      className={cn(
        "grid duration-200 ease-out motion-reduce:transition-none",
        open
          ? "visible grid-rows-[1fr] opacity-100 transition-[grid-template-rows,opacity]"
          : "invisible grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity,visibility]",
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}
